import AnalysisResult from '../components/AnalysisResult';
import React, { useState, useEffect } from 'react';
import PriceChart       from "../components/PriceChart";
import NewsFeed         from "../components/NewsFeed";
import EarningsPanel    from "../components/EarningsPanel";
import AnalystConsensus from "../components/AnalystConsensus";
import HeroCard         from '../components/stock/HeroCard';
import TickerFilingsSummary from '../components/filings/TickerFilingsSummary'; // SEC-FILINGS-PAGE — confirmed design: grouped by form label, per-group EDGAR link
import TickerFinancialsSummary from '../components/financials/TickerFinancialsSummary'; // SCREENER-FINANCIALS — new component, mirrors TickerFilingsSummary's fetch/loading/error shape
import OwnershipTab from '../components/ownership/OwnershipTab'; // OWNERSHIP-CAPTURE-ARC — replaces the "insiders" Coming Soon placeholder

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

const DelayedBadge = () => (
  <div style={{ display: 'inline-flex', alignItems: 'center', gap: '0.4rem',
    padding: '0.3rem 0.75rem', background: 'rgba(255,170,0,0.1)',
    border: '1px solid rgba(255,170,0,0.3)', borderRadius: '20px',
    fontSize: '0.7rem', fontWeight: 700, color: '#ffaa00', letterSpacing: '0.05em' }}>
    ⏱ 15-MIN DELAYED · NOT REAL-TIME
  </div>
);

const DisclaimerBadge = () => (
  <div style={{ display: 'inline-flex', alignItems: 'center', gap: '0.4rem',
    padding: '0.3rem 0.75rem', background: 'rgba(100,116,139,0.1)',
    border: '1px solid rgba(100,116,139,0.2)', borderRadius: '20px',
    fontSize: '0.7rem', fontWeight: 600, color: '#64748b' }}>
    🤖 Algorithmic signal · Not investment advice
  </div>
);

const StatCard = ({ label, value }) => (
  <div style={{ padding: '1rem', background: 'rgba(255,255,255,0.03)',
    border: '1px solid rgba(255,255,255,0.06)', borderRadius: '10px' }}>
    <div style={{ fontSize: '0.7rem', color: '#64748b', fontWeight: 600,
      letterSpacing: '0.08em', marginBottom: '0.4rem' }}>{label}</div>
    <div style={{ fontSize: '1.1rem', fontWeight: 700, color: '#f8fafc' }}>{value != null ? value : '—'}</div>
  </div>
);

// ── Tab definitions ──────────────────────────────────────────────────────────
const TABS = [
  { id: 'overview',   label: 'Overview',   locked: false },
  { id: 'technicals', label: 'Technicals', locked: false },
  { id: 'news',       label: 'News',       locked: false },
  { id: 'ownership',  label: 'Ownership',  locked: true  }, // OWNERSHIP-CAPTURE-ARC — was 'insiders'/'Insiders', broadened since this tab now covers Form 4 + future 13F/13D-G, not insiders alone
  { id: 'filings',    label: 'Filings',    locked: true  },
  { id: 'financials', label: 'Financials', locked: true  }, // SCREENER-FINANCIALS
];

export default function StockPage({ ticker, isLoggedIn, token, onRequestInvite, onSignIn, onNavigate, onAnalyze, autoRunAnalysis, onAutoRunDone, initialAnalysis, onInitialAnalysisConsumed }) {
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState('');
  const [activeTab,      setActiveTab]      = useState('overview');
  const [inWatchlist,    setInWatchlist]    = useState(false);
  const [shareLoading,   setShareLoading]   = useState(false);
  const [shareCopied,    setShareCopied]    = useState(false);
  const [memberAnalysis, setMemberAnalysis] = useState(null);
  const [memberLoading,  setMemberLoading]  = useState(false);
  const [memberError,    setMemberError]    = useState('');

  const runFullAnalysis = async () => {
    if (!token) return;
    setMemberLoading(true);
    setMemberError('');
    try {
      const res = await fetch(API_URL + '/api/stocks/analyze', {
        method: 'POST',
        headers: { 'Authorization': 'Bearer ' + token, 'Content-Type': 'application/json' },
        body: JSON.stringify({ ticker })
      });
      const d = await res.json();
      if (d.success && d.data) {
        setMemberAnalysis(d.data);
        setMemberError('');
      } else if (res.status === 429) {
        setMemberError('LIMIT_REACHED:' + (d.message || 'Monthly analysis limit reached'));
      } else if (res.status === 403) {
        setMemberError('UNVERIFIED:' + (d.message || 'Please verify your email first'));
      } else {
        setMemberError(d.message || 'Analysis failed — please try again');
      }
    } catch (e) {
      setMemberError('Network error — please try again');
    }
    setMemberLoading(false);
  };

  const handleShare = async () => {
    setShareLoading(true);
    try {
      const headers = { 'Content-Type': 'application/json' };
      if (token) headers['Authorization'] = 'Bearer ' + token;
      const res = await fetch(API_URL + '/api/share', {
        method: 'POST',
        headers,
        body: JSON.stringify({
          ticker:      ticker,
          signal:      data.signal,
          score:       data.score,
          price:       data.price || null,
          companyName: data.companyName || ticker
        })
      });
      const d = await res.json();
      if (d.success) {
        const url = window.location.origin + '/share/' + d.shareId;
        navigator.clipboard.writeText(url).catch(() => {});
        setShareCopied(true);
        setTimeout(() => setShareCopied(false), 3000);
        if (onNavigate) onNavigate('share', { shareId: d.shareId });
      }
    } catch (e) {
      console.error('Share failed:', e);
    }
    setShareLoading(false);
  };

  const [wlLoading,    setWlLoading]    = useState(false);
  const [searchTicker, setSearchTicker] = useState('');
  const [searchError,  setSearchError]  = useState('');

  // Auto-run analysis after sign-in redirect
  useEffect(() => {
    if (autoRunAnalysis && token && !loading && data) {
      onAutoRunDone && onAutoRunDone();
      runFullAnalysis();
    }
  }, [autoRunAnalysis, token, loading, data]); // eslint-disable-line

  // Reset all tab-local state when ticker changes
  useEffect(() => {
    setActiveTab('overview');
    setMemberAnalysis(null);
    setMemberError('');
    setMemberLoading(false);
  }, [ticker]);

  // Consume initialAnalysis passed from home page analyze — no re-run, no credit wasted
  useEffect(() => {
    if (initialAnalysis && !memberAnalysis) {
      setMemberAnalysis(initialAnalysis);
      if (onInitialAnalysisConsumed) onInitialAnalysisConsumed();
    }
  }, [initialAnalysis]); // eslint-disable-line

  useEffect(() => {
    if (!ticker) { setLoading(false); return; }
    setLoading(true);
    setError('');
    setData(null);
    fetch(API_URL + '/api/seo/stocks/' + ticker)
      .then(r => r.json())
      .then(res => {
        if (!res.success) { setError(res.message || 'Stock not found'); return; }
        setData(res);
        document.title = res.companyName + ' (' + ticker + ') Stock Signal — MarketPulze';
        const desc = document.querySelector('meta[name="description"]');
        if (desc) desc.setAttribute('content',
          res.companyName + ' algorithmic signal: ' + res.signal + ' (score ' + res.score + '/100). Delayed market data and AI analysis.');
        let canon = document.querySelector('link[rel="canonical"]');
        if (!canon) { canon = document.createElement('link'); canon.rel = 'canonical'; document.head.appendChild(canon); }
        canon.href = 'https://marketpulze.com/stocks/' + ticker;
      })
      .catch(() => setError('Failed to load stock data'))
      .finally(() => setLoading(false));
    if (token) {
      fetch((process.env.REACT_APP_API_URL || 'http://localhost:5000') + '/api/watchlist', {
        headers: { Authorization: 'Bearer ' + token }
      }).then(r => r.json()).then(d => {
        if (d.success) setInWatchlist((d.tickers || []).includes(ticker));
      }).catch(() => {});
    }
  }, [ticker, token]);

  const fmt = (n) => {
    if (n == null || n === 0) return null;
    return Number(n).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  // ── Locked tab gate (guests) ─────────────────────────────────────────────
  const LockedTabGate = ({ tabId }) => (
    <div style={{ textAlign: 'center', padding: '3.5rem 1.5rem',
      background: 'rgba(0,0,0,0.3)', border: '1px solid rgba(255,255,255,0.06)',
      borderRadius: '14px' }}>
      <div style={{ fontSize: '2rem', marginBottom: '1rem' }}>🔒</div>
      <div style={{ fontSize: '1rem', fontWeight: 700, color: '#f8fafc', marginBottom: '0.5rem' }}>
        {tabId === 'ownership' ? 'Insider & Institutional Ownership' : tabId === 'financials' ? 'Financial Statements' : 'SEC Filings'} — Members Only
      </div>
      <div style={{ fontSize: '0.85rem', color: '#64748b', maxWidth: '380px',
        margin: '0 auto 1.5rem', lineHeight: 1.6 }}>
        {tabId === 'ownership'
          ? 'Track what directors, officers, and major shareholders are buying and selling in real time.'
          : tabId === 'financials'
          ? 'Full income statement, balance sheet, cash flow, and computed ratios sourced directly from SEC XBRL filings.'
          : 'Access 10-K, 10-Q, 8-K, and all SEC EDGAR filings for this company.'}
      </div>
      <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'center', flexWrap: 'wrap' }}>
        <button onClick={onRequestInvite}
          style={{ padding: '0.65rem 1.25rem', background: 'transparent',
            border: '1px solid rgba(0,255,136,0.4)', borderRadius: '8px',
            color: '#00ff88', fontWeight: 700, cursor: 'pointer',
            fontSize: '0.85rem', fontFamily: 'inherit' }}>
          Request Invite
        </button>
        <button onClick={onSignIn}
          style={{ padding: '0.65rem 1.25rem',
            background: 'linear-gradient(135deg,#00ff88,#00d4ff)',
            border: 'none', borderRadius: '8px', color: '#050d1a',
            fontWeight: 800, cursor: 'pointer', fontSize: '0.85rem',
            fontFamily: 'inherit' }}>
          Sign In
        </button>
      </div>
    </div>
  );

  // ── Ownership tab content (members only — same locked-tab pattern as
  //    Filings/Financials above). Local component so useTickerOwnership only
  //    fetches when this tab is actually active/mounted. ── OWNERSHIP-CAPTURE-ARC
  //    Was the ComingSoonTab placeholder for 'insiders' — real content now that
  //    is code-complete (0 rows until its first real run; OwnershipTab.jsx itself
  //    handles that explicit "no data yet" state, not this file).
  const OwnershipTabPanel = () => (
    <OwnershipTab ticker={ticker} token={token} />
  );

  // ── Filings tab content (members only — full lock unchanged for guests,
  //    Pattern A stays here per that decision; standalone /filings page uses
  //    Pattern B partial gating instead). Local component (not top-level) so
  //    useFilingsData only fetches when this tab is actually active/mounted —
  //    matches how LockedTabGate is already scoped as a local in this file.
  //    in this file. ── SEC-FILINGS-PAGE
  const FilingsTabPanel = () => (
    <TickerFilingsSummary ticker={ticker} token={token} onNavigate={onNavigate} />
  );

  // ── Financials tab content (members only — same locked-tab pattern as
  //    Insiders/Filings above). Local component so useTickerFinancials only
  //    fetches when this tab is actually active/mounted. ── SCREENER-FINANCIALS
  const FinancialsTabPanel = () => (
    <TickerFinancialsSummary ticker={ticker} onNavigate={onNavigate} />
  );

  return (
    <div>

      {/* ── Top badges ── */}
      <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', marginBottom: '1.25rem' }}>
        <DelayedBadge />
        <DisclaimerBadge />
      </div>

      {/* ── Back button ── */}
      <button onClick={() => onNavigate('home')}
        style={{ background: 'none', border: 'none', color: '#64748b', cursor: 'pointer',
          fontSize: '0.85rem', padding: '0 0 1rem 0', fontFamily: 'inherit' }}>
        ← Back to Market
      </button>

      {/* ── Quick ticker search ── */}
      <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1.25rem', maxWidth: '420px' }}>
        <input
          type="text"
          value={searchTicker}
          onChange={e => { setSearchTicker(e.target.value.toUpperCase()); setSearchError(''); }}
          onKeyPress={e => {
            if (e.key === 'Enter') {
              if (!searchTicker.trim()) { setSearchError('Enter a ticker'); return; }
              if (onAnalyze) { onAnalyze(searchTicker.trim()); setSearchTicker(''); }
              else onNavigate('stock', { ticker: searchTicker.trim() });
            }
          }}
          placeholder="Search another ticker…"
          style={{ flex: 1, padding: '0.55rem 1rem', background: 'rgba(15,30,60,0.6)',
            border: '1px solid rgba(0,212,255,0.2)', borderRadius: '8px',
            color: '#f8fafc', fontSize: '0.85rem', outline: 'none', fontFamily: 'inherit' }} />
        <button
          onClick={() => {
            if (!searchTicker.trim()) { setSearchError('Enter a ticker'); return; }
            if (onAnalyze) { onAnalyze(searchTicker.trim()); setSearchTicker(''); }
            else onNavigate('stock', { ticker: searchTicker.trim() });
          }}
          style={{ padding: '0.55rem 1.1rem', background: 'linear-gradient(135deg,#00ff88,#00d4ff)',
            border: 'none', borderRadius: '8px', color: '#050d1a', fontWeight: 800,
            cursor: 'pointer', fontSize: '0.82rem', fontFamily: 'inherit', whiteSpace: 'nowrap' }}>
          🔮 Analyze
        </button>
      </div>
      {searchError && (
        <div style={{ fontSize: '0.75rem', color: '#ff4444', marginBottom: '0.75rem' }}>
          {searchError}
        </div>
      )}

      {/* ── Loading ── */}
      {loading && (
        <div style={{ textAlign: 'center', padding: '4rem', color: '#64748b' }}>
          <div style={{ fontSize: '2rem', marginBottom: '1rem' }}>⏳</div>
          Loading {ticker} data...
        </div>
      )}

      {/* ── Error ── */}
      {error && (
        <div style={{ textAlign: 'center', padding: '4rem' }}>
          <div style={{ fontSize: '2rem', marginBottom: '1rem' }}>⚠️</div>
          <div style={{ color: '#ff4444', marginBottom: '1rem' }}>{error}</div>
          <button onClick={() => onNavigate('home')}
            style={{ padding: '0.75rem 1.5rem', background: 'rgba(0,212,255,0.1)',
              border: '1px solid rgba(0,212,255,0.3)', borderRadius: '8px',
              color: '#00d4ff', cursor: 'pointer', fontFamily: 'inherit', fontWeight: 700 }}>
            Back to Market
          </button>
        </div>
      )}

      {/* ── Main content ── */}
      {data && !loading && (() => {
        const marketClosed    = !data.quote.price || data.quote.price === 0;
        const displayPrice    = marketClosed ? data.quote.prevClose : data.quote.price;
        const displayChange   = marketClosed ? 0 : (data.quote.change || 0);
        const displayChangePct= marketClosed ? 0 : (data.quote.changePercent || 0);
        const priceStr        = fmt(displayPrice)         ? '$' + fmt(displayPrice)         : '—';
        const prevCloseStr    = fmt(data.quote.prevClose) ? '$' + fmt(data.quote.prevClose) : '—';

        return (
          <>
            {/* ── Company header — always visible above tabs ── */}
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: '1rem',
              marginBottom: '1.5rem', flexWrap: 'wrap' }}>
              {data.logo && (
                <img src={data.logo} alt={data.companyName + ' logo'}
                  style={{ width: 52, height: 52, borderRadius: '10px',
                    background: '#fff', padding: '4px', objectFit: 'contain' }}
                  onError={e => { e.target.style.display = 'none'; }} />
              )}
              <div style={{ flex: 1 }}>
                <h1 style={{ margin: 0, fontSize: '1.6rem', fontWeight: 900, color: '#f8fafc',
                  letterSpacing: '-0.02em', lineHeight: 1.1 }}>
                  {data.companyName}
                  <span style={{ fontSize: '1rem', color: '#64748b', fontWeight: 600,
                    marginLeft: '0.5rem' }}>({ticker})</span>
                </h1>
                <div style={{ fontSize: '0.8rem', color: '#475569', marginTop: '0.3rem' }}>
                  {data.sector} · {data.exchange}
                </div>
              </div>
            </div>

            {/* ── HeroCard — always visible above tabs ── */}
            <HeroCard
              priceStr={priceStr}
              displayChange={displayChange}
              displayChangePct={displayChangePct}
              marketClosed={marketClosed}
              prevCloseStr={prevCloseStr}
              score={data.score}
              signal={data.signal}
              handleShare={handleShare}
              shareLoading={shareLoading}
              shareCopied={shareCopied}
              isLoggedIn={isLoggedIn}
              token={token}
              ticker={ticker}
              inWatchlist={inWatchlist}
              setInWatchlist={setInWatchlist}
              wlLoading={wlLoading}
              setWlLoading={setWlLoading}
              data={data}
              memberAnalysis={memberAnalysis}
              onNavigate={onNavigate}
            />

            {/* ── TAB BAR ── */}
            <div style={{ display: 'flex', borderBottom: '1px solid rgba(255,255,255,0.08)',
              marginBottom: '1.75rem', overflowX: 'auto', gap: 0, marginTop: '0.5rem' }}>
              {TABS.map(tab => {
                const guestLocked = tab.locked && !isLoggedIn;
                const isActive    = activeTab === tab.id;
                return (
                  <button key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    style={{
                      padding: '0.75rem 1.15rem',
                      background: 'none',
                      border: 'none',
                      borderBottom: isActive
                        ? '2px solid #00d4ff'
                        : '2px solid transparent',
                      color: isActive
                        ? '#00d4ff'
                        : guestLocked
                          ? '#374151'
                          : '#64748b',
                      fontWeight: isActive ? 700 : 500,
                      cursor: 'pointer',
                      fontSize: '0.83rem',
                      fontFamily: 'inherit',
                      letterSpacing: '0.01em',
                      whiteSpace: 'nowrap',
                      marginBottom: '-1px',
                      transition: 'color 0.15s',
                    }}
                    onMouseEnter={e => { if (!isActive) e.currentTarget.style.color = guestLocked ? '#4b5563' : '#94a3b8'; }}
                    onMouseLeave={e => { if (!isActive) e.currentTarget.style.color = guestLocked ? '#374151' : '#64748b'; }}>
                    {tab.label}{guestLocked ? ' 🔒' : ''}
                  </button>
                );
              })}
            </div>

            {/* ══════════════════════════════════════════════════
                OVERVIEW TAB
                Stats · AI Signal · Member Analysis · Related · Disclaimer
            ══════════════════════════════════════════════════ */}
            {activeTab === 'overview' && (
              <>
                {/* ── Stats grid (hidden once member analysis loads) ── */}
                {!memberAnalysis && (() => {
                  const f = data.fundamentals || {};
                  const fmtMarketCap = (n) => {
                    if (!n) return '—';
                    if (n >= 1000000) return '$' + (n / 1000000).toFixed(2) + 'T';
                    if (n >= 1000)    return '$' + (n / 1000).toFixed(1) + 'B';
                    return '$' + n.toFixed(0) + 'M';
                  };
                  const fmtNum = (n, dp = 2, prefix = '') =>
                    n != null ? prefix + Number(n).toFixed(dp) : '—';
                  const fmtPct = (n) => n != null ? (Number(n) * 100).toFixed(2) + '%' : '—';
                  const w52High = f.week52High ?? data.quote.high;
                  const w52Low  = f.week52Low  ?? data.quote.low;
                  return (
                    <>
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(150px,1fr))',
                        gap: '0.75rem', marginBottom: f._source === 'cache' || f._source === 'unavailable' ? '0.4rem' : '1.75rem' }}>
                        <StatCard label="PREV CLOSE"  value={prevCloseStr} />
                        <StatCard label="MARKET CAP"  value={fmtMarketCap(f.marketCap ?? data.marketCap)} />
                        <StatCard label="P/E RATIO"   value={fmtNum(f.peRatio, 1)} />
                        <StatCard label="EPS (TTM)"   value={fmtNum(f.eps, 2, '$')} />
                        <StatCard label="52W HIGH"    value={w52High ? '$' + Number(w52High).toFixed(2) : '—'} />
                        <StatCard label="52W LOW"     value={w52Low  ? '$' + Number(w52Low).toFixed(2)  : '—'} />
                        <StatCard label="BETA"        value={fmtNum(f.beta, 2)} />
                        <StatCard label="DIV YIELD"   value={fmtPct(f.dividendYield)} />
                        <StatCard label="FORWARD P/E" value={fmtNum(f.forwardPE, 1)} />
                        <StatCard label="PRICE/BOOK"  value={fmtNum(f.priceToBook, 2)} />
                        <StatCard label="SECTOR"      value={data.sector} />
                        {/* Screen similar stocks in same sector */}
                        {data.sector && data.sector !== 'N/A' && (
                          <button
                            onClick={() => onNavigate && onNavigate('screener')}
                            title={`Screen ${data.sector} stocks by signal`}
                            style={{
                              padding: '0.35rem 0.8rem', borderRadius: 6, cursor: 'pointer',
                              background: 'rgba(99,102,241,0.1)', border: '1px solid rgba(99,102,241,0.25)',
                              color: '#818cf8', fontSize: '0.72rem', fontWeight: 700,
                              fontFamily: 'inherit', whiteSpace: 'nowrap',
                            }}
                          >
                            Screen {data.sector} →
                          </button>
                        )}
                        <StatCard label="SIGNAL SCORE" value={data.score + ' / 100'} />
                      </div>
                      {f._source === 'cache' && f._cachedAt && (
                        <div style={{ textAlign: 'right', marginBottom: '1.75rem' }}>
                          <span style={{ fontSize: '0.7rem', color: '#64748b',
                            background: 'rgba(100,116,139,0.08)', border: '1px solid rgba(100,116,139,0.15)',
                            borderRadius: '20px', padding: '0.2rem 0.7rem' }}>
                            📦 Key metrics cached · last updated{' '}
                            {new Date(f._cachedAt).toLocaleString('en-US', {
                              month: 'short', day: 'numeric',
                              hour: '2-digit', minute: '2-digit', timeZoneName: 'short'
                            })}
                          </span>
                        </div>
                      )}
                      {f._source === 'unavailable' && (
                        <div style={{ textAlign: 'right', marginBottom: '1.75rem' }}>
                          <span style={{ fontSize: '0.7rem', color: '#64748b',
                            background: 'rgba(100,116,139,0.08)', border: '1px solid rgba(100,116,139,0.15)',
                            borderRadius: '20px', padding: '0.2rem 0.7rem' }}>
                            ⚠️ Key metrics temporarily unavailable
                          </span>
                        </div>
                      )}
                    </>
                  );
                })()}

                {/* ── AI Signal Section ── */}
                <div style={{ marginBottom: '1.75rem', padding: '1.5rem',
                  background: 'rgba(0,212,255,0.04)', border: '1px solid rgba(0,212,255,0.12)',
                  borderRadius: '14px' }}>

                  <div style={{ fontSize: '0.7rem', fontWeight: 700, color: '#00d4ff',
                    letterSpacing: '0.1em', marginBottom: '0.75rem' }}>🤖 AI SIGNAL SUMMARY</div>
                  <p style={{ margin: '0 0 0.75rem 0', color: '#cbd5e1', lineHeight: 1.7, fontSize: '0.95rem' }}>
                    {data.aiTeaser}
                  </p>
                  <div style={{ padding: '0.65rem 1rem', background: 'rgba(255,170,0,0.05)',
                    border: '1px solid rgba(255,170,0,0.15)', borderRadius: '8px',
                    fontSize: '0.72rem', color: '#78716c', lineHeight: 1.6, marginBottom: '1rem' }}>
                    ⚠️ <strong style={{ color: '#a8a29e' }}>Algorithmic output.</strong> This text is generated by automated algorithms, not a human analyst. It may contain errors or omissions and should not be relied upon as accurate or complete.
                  </div>

                  {/* Member analysis loaded — show re-run option */}
                  {memberAnalysis && (
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                      flexWrap: 'wrap', gap: '0.75rem', padding: '0.75rem 1rem',
                      background: 'rgba(0,255,136,0.05)', border: '1px solid rgba(0,255,136,0.15)',
                      borderRadius: '8px' }}>
                      <span style={{ fontSize: '0.8rem', color: '#00ff88', fontWeight: 600 }}>✅ Full analysis loaded</span>
                      <button onClick={memberLoading ? undefined : runFullAnalysis}
                        style={{ padding: '0.4rem 1rem', background: 'rgba(0,212,255,0.1)',
                          border: '1px solid rgba(0,212,255,0.3)', borderRadius: '6px',
                          color: '#00d4ff', fontWeight: 700, cursor: memberLoading ? 'default' : 'pointer',
                          fontSize: '0.78rem', fontFamily: 'inherit' }}>
                        {memberLoading ? '⏳ Re-running…' : '🔄 Re-run Analysis'}
                      </button>
                    </div>
                  )}

                  {/* Error state */}
                  {memberError && !memberAnalysis && (() => {
                    const isLimit      = memberError.startsWith('LIMIT_REACHED:');
                    const isUnverified = memberError.startsWith('UNVERIFIED:');
                    const msg = memberError.replace(/^(LIMIT_REACHED:|UNVERIFIED:)/, '');
                    return (
                      <div style={{ padding: '1rem 1.25rem',
                        background: isLimit ? 'rgba(255,170,0,0.08)' : 'rgba(239,68,68,0.08)',
                        border: `1px solid ${isLimit ? 'rgba(255,170,0,0.25)' : 'rgba(239,68,68,0.2)'}`,
                        borderRadius: '10px', marginBottom: '0.75rem' }}>
                        <div style={{ color: isLimit ? '#ffaa00' : '#ef4444',
                          fontSize: '0.88rem', fontWeight: 600, marginBottom: '0.5rem' }}>
                          {isLimit ? '📊 ' : isUnverified ? '📧 ' : '⚠️ '}{msg}
                        </div>
                        {isLimit && (
                          <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                            <button onClick={() => onNavigate('pricing')}
                              style={{ padding: '0.4rem 1rem',
                                background: 'linear-gradient(135deg,#00ff88,#00d4ff)',
                                border: 'none', borderRadius: '6px', color: '#050d1a',
                                fontSize: '0.78rem', fontWeight: 800,
                                cursor: 'pointer', fontFamily: 'inherit' }}>
                              Upgrade Plan →
                            </button>
                          </div>
                        )}
                        {isUnverified && (
                          <div style={{ fontSize: '0.78rem', color: '#94a3b8' }}>
                            Check your inbox for a verification email, then try again.
                          </div>
                        )}
                        {!isLimit && !isUnverified && (
                          <button onClick={runFullAnalysis}
                            style={{ padding: '0.35rem 0.85rem',
                              background: 'rgba(239,68,68,0.15)',
                              border: '1px solid rgba(239,68,68,0.3)', borderRadius: '6px',
                              color: '#ef4444', fontSize: '0.78rem', fontWeight: 700,
                              cursor: 'pointer', fontFamily: 'inherit' }}>
                            Try Again
                          </button>
                        )}
                      </div>
                    );
                  })()}

                  {/* Gate — only when no analysis yet and no error */}
                  {!memberAnalysis && !memberError && (
                    <div style={{ padding: '1.25rem', background: 'rgba(0,0,0,0.3)',
                      border: '1px solid rgba(255,255,255,0.06)', borderRadius: '10px',
                      textAlign: 'center' }}>
                      <div style={{ fontSize: '0.85rem', color: '#64748b', marginBottom: '0.75rem' }}>
                        🔒 Full AI analysis, bull/bear breakdown and sector context — members only
                      </div>
                      {isLoggedIn ? (
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.5rem' }}>
                          <button onClick={memberLoading ? undefined : runFullAnalysis}
                            disabled={memberLoading}
                            style={{ padding: '0.65rem 1.75rem',
                              background: memberLoading
                                ? 'rgba(100,116,139,0.3)'
                                : 'linear-gradient(135deg,#00ff88,#00d4ff)',
                              border: 'none', borderRadius: '8px', color: '#050d1a',
                              fontWeight: 800, cursor: memberLoading ? 'default' : 'pointer',
                              fontSize: '0.9rem', fontFamily: 'inherit', minWidth: '200px' }}>
                            {memberLoading ? '⏳ Analysing…' : '🔮 Run Full Analysis'}
                          </button>
                          <span style={{ fontSize: '0.7rem', color: '#475569' }}>Uses 1 of your monthly analyses</span>
                        </div>
                      ) : (
                        <div>
                          <div style={{ fontSize: '0.78rem', color: '#475569', marginBottom: '0.85rem' }}>
                            Members get full AI breakdown: score components, bull/bear signals, model estimate &amp; more
                          </div>
                          <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'center', flexWrap: 'wrap' }}>
                            <button onClick={onRequestInvite}
                              style={{ padding: '0.65rem 1.25rem', background: 'transparent',
                                border: '1px solid rgba(0,255,136,0.4)', borderRadius: '8px',
                                color: '#00ff88', fontWeight: 700, cursor: 'pointer',
                                fontSize: '0.85rem', fontFamily: 'inherit' }}>
                              Request Invite
                            </button>
                            <button onClick={onSignIn}
                              style={{ padding: '0.65rem 1.25rem',
                                background: 'linear-gradient(135deg,#00ff88,#00d4ff)',
                                border: 'none', borderRadius: '8px', color: '#050d1a',
                                fontWeight: 800, cursor: 'pointer', fontSize: '0.85rem',
                                fontFamily: 'inherit' }}>
                              Sign In
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* ── Inline Member Analysis ── */}
                {memberAnalysis && (
                  <div style={{ marginBottom: '1.5rem' }}>
                    <AnalysisResult
                      analysis={memberAnalysis}
                      token={token}
                      onSave={() => {}}
                      navigateTo={onNavigate}
                      inline={true}
                    />
                  </div>
                )}

                {/* ── Related stocks ── */}
                {data.related && data.related.length > 0 && (
                  <div style={{ marginBottom: '2rem' }}>
                    <div style={{ fontSize: '0.75rem', fontWeight: 700, color: '#475569',
                      letterSpacing: '0.08em', marginBottom: '0.75rem' }}>
                      EXPLORE OTHER S&P 500 STOCKS
                    </div>
                    <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                      {data.related.map(t => (
                        <button key={t} onClick={() => onNavigate('stock', { ticker: t })}
                          style={{ padding: '0.4rem 0.9rem', background: 'rgba(255,255,255,0.04)',
                            border: '1px solid rgba(255,255,255,0.08)', borderRadius: '20px',
                            color: '#94a3b8', cursor: 'pointer', fontSize: '0.8rem',
                            fontWeight: 600, fontFamily: 'inherit', transition: 'all 0.15s' }}
                          onMouseEnter={e => { e.currentTarget.style.color = '#00d4ff'; e.currentTarget.style.borderColor = 'rgba(0,212,255,0.3)'; }}
                          onMouseLeave={e => { e.currentTarget.style.color = '#94a3b8'; e.currentTarget.style.borderColor = 'rgba(255,255,255,0.08)'; }}>
                          {t}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* ── Disclaimer ── */}
                <div style={{ padding: '1rem 1.25rem', background: 'rgba(255,255,255,0.02)',
                  border: '1px solid rgba(255,255,255,0.05)', borderRadius: '10px',
                  fontSize: '0.72rem', color: '#475569', lineHeight: 1.6 }}>
                  <strong style={{ color: '#64748b' }}>Disclaimer:</strong>{' '}
                  {!isLoggedIn && (
                    <span>Data is delayed approximately 15 minutes. </span>
                  )}
                  This is an algorithmic signal only and does not constitute investment advice. Past signals do not guarantee future results. MarketPulze provides algorithmic signals for informational purposes only. Nothing on this page constitutes financial advice, a recommendation to buy or sell any security, or an offer to provide investment advisory services. Always conduct your own research and consult a qualified financial advisor before making investment decisions.
                </div>
              </>
            )}

            {/* ══════════════════════════════════════════════════
                TECHNICALS TAB
                Price Chart · Earnings · Analyst Consensus
            ══════════════════════════════════════════════════ */}
            {activeTab === 'technicals' && (
              <>
                {/* Advanced Chart link — BL-OCI3-02 restored */}
                <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '0.75rem' }}>
                  <button onClick={() => onNavigate && onNavigate('chart', { ticker })}
                    style={{ background: 'none', border: '1px solid rgba(0,212,255,0.3)',
                      borderRadius: '8px', color: '#00d4ff', padding: '0.4rem 1rem',
                      fontSize: '0.78rem', fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}>
                    Advanced Chart →
                  </button>
                </div>
                {/* 22B: Price Chart */}
                <PriceChart ticker={ticker} />
                <div style={{ display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
                  gap: 24, marginTop: '0.5rem' }}>
                  <EarningsPanel    ticker={ticker} />
                  <AnalystConsensus ticker={ticker} />
                </div>
              </>
            )}

            {/* ══════════════════════════════════════════════════
                NEWS TAB
            ══════════════════════════════════════════════════ */}
            {activeTab === 'news' && (
              <NewsFeed ticker={ticker} />
            )}

            {/* ══════════════════════════════════════════════════
                OWNERSHIP TAB — OWNERSHIP-CAPTURE-ARC (was INSIDERS, Coming Soon)
            ══════════════════════════════════════════════════ */}
            {activeTab === 'ownership' && (
              isLoggedIn
                ? <OwnershipTabPanel />
                : <LockedTabGate tabId="ownership" />
            )}

            {/* ══════════════════════════════════════════════════
                FILINGS TAB — SEC-FILINGS-PAGE
            ══════════════════════════════════════════════════ */}
            {activeTab === 'filings' && (
              isLoggedIn
                ? <FilingsTabPanel />
                : <LockedTabGate tabId="filings" />
            )}

            {/* ══════════════════════════════════════════════════
                FINANCIALS TAB — SCREENER-FINANCIALS
            ══════════════════════════════════════════════════ */}
            {activeTab === 'financials' && (
              isLoggedIn
                ? <FinancialsTabPanel />
                : <LockedTabGate tabId="financials" />
            )}
          </>
        );
      })()}
    </div>
  );
}

